/**
 * Room codes: six characters a player can read down a telephone.
 *
 * Crockford's base-32 alphabet, which exists for exactly this problem — it
 * drops `I`, `L`, `O` and `U` and specifies how the excluded characters fold
 * back in (`I`/`L` → `1`, `O` → `0`, `U` → `V`), so someone who mis-hears a
 * digit for a letter still joins the right room. 32 symbols over 6 places is
 * a billion rooms against a relay that holds a handful open at a time.
 */

export const ROOM_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export const ROOM_CODE_LENGTH = 6;

/** Crockford's decoding equivalences, plus nothing invented on top of them. */
const LOOKALIKES: Readonly<Record<string, string>> = { I: "1", L: "1", O: "0", U: "V" };

function fold(character: string): string {
  const folded = LOOKALIKES[character] ?? character;
  return ROOM_CODE_ALPHABET.includes(folded) ? folded : "";
}

/** Upper-cases, drops separators and anything else off the alphabet, and folds the lookalikes. */
export function normalizeRoomCode(value: string): string {
  return [...value.toUpperCase()].map(fold).join("").slice(0, ROOM_CODE_LENGTH);
}

export function isRoomCode(value: string): boolean {
  return value.length === ROOM_CODE_LENGTH && [...value].every((character) => ROOM_CODE_ALPHABET.includes(character));
}

/**
 * Six symbols drawn from `random` (0 ≤ r < 1). Injected rather than calling
 * `Math.random` directly so the generator is testable, and so nothing in this
 * package reaches for an ambient source of entropy.
 */
export function makeRoomCode(random: () => number): string {
  let code = "";
  for (let index = 0; index < ROOM_CODE_LENGTH; index++) {
    const position = Math.min(ROOM_CODE_ALPHABET.length - 1, Math.floor(random() * ROOM_CODE_ALPHABET.length));
    code += ROOM_CODE_ALPHABET[position];
  }
  return code;
}
